import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import styles from './Clientes.module.css';
import FichaCliente from './FichaCliente';
import { supabase } from '../../../../Supabase/SupabaseClient';

const FORMULARIO_VACIO = {
  nombre: '',
  email: '',
  dni: '',
  telefono: '',
  direccion: '',
};

const CAMPOS = ['nombre', 'email', 'dni', 'telefono', 'direccion'];

const FOCUSEABLES =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const iniciales = (nombre) =>
  (nombre || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || '')
    .join('')
    .toUpperCase() || '?';

const porNombre = (a, b) =>
  (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });

function Clientes() {
  const [usuarios, setUsuarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const [modalAbierto, setModalAbierto] = useState(false);
  const [formData, setFormData] = useState({ ...FORMULARIO_VACIO });
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [erroresCampo, setErroresCampo] = useState({});

  const modalRef = useRef(null);
  const primerCampoRef = useRef(null);

  // ── Carga ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('usuarios')
        .select('*')
        .eq('rol', 'usuario')
        .order('nombre', { ascending: true });
      if (err) throw err;
      setUsuarios(data || []);
    } catch (err) {
      setError('No se pudieron cargar los clientes: ' + (err.message || JSON.stringify(err)));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(''), 6000);
    return () => clearTimeout(t);
  }, [aviso]);

  const usuariosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return usuarios;
    return usuarios.filter((u) =>
      [u.nombre, u.email, u.telefono, u.dni, u.direccion].some((campo) =>
        campo && String(campo).toLowerCase().includes(term)
      )
    );
  }, [usuarios, busqueda]);

  const actualizarClienteEnLista = (id, patch) => {
    setUsuarios((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ...patch } : u)).sort(porNombre)
    );
    setSelectedCliente((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  // ── Modal ────────────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setFormData({ ...FORMULARIO_VACIO });
    setEditingId(null);
    setFormError('');
    setErroresCampo({});
    setModalAbierto(true);
  };

  const abrirEdicion = (cliente) => {
    setFormData({
      nombre: cliente.nombre || '',
      email: cliente.email || '',
      dni: cliente.dni || '',
      telefono: cliente.telefono || '',
      direccion: cliente.direccion || '',
    });
    setEditingId(cliente.id);
    setFormError('');
    setErroresCampo({});
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    if (guardando) return;
    setModalAbierto(false);
    setFormError('');
    setErroresCampo({});
  };

  // Escape para cerrar, scroll bloqueado, foco atrapado en el modal y devuelto
  // al botón de origen al salir.
  useEffect(() => {
    if (!modalAbierto) return undefined;

    const focoPrevio = document.activeElement;
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      if (primerCampoRef.current) primerCampoRef.current.focus();
    }, 40);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrarModal();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusables = Array.from(modalRef.current.querySelectorAll(FOCUSEABLES));
      if (focusables.length === 0) return;
      const primero = focusables[0];
      const ultimo = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflowPrevio;
      if (focoPrevio && typeof focoPrevio.focus === 'function') focoPrevio.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalAbierto, guardando]);

  // ── Formulario ───────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErroresCampo((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validar = () => {
    const errs = {};
    const nombre = formData.nombre.trim();
    const email = formData.email.trim();
    const dni = formData.dni.trim();
    const tel = formData.telefono.trim();

    if (!nombre) errs.nombre = 'Ingresá el nombre del cliente.';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      errs.email = 'Revisá el formato del email.';
    }
    if (dni && !/^[0-9.\-\s]{6,15}$/.test(dni)) {
      errs.dni = 'El DNI lleva solo números, puntos o guiones.';
    }
    if (tel && !/^[0-9()+\-\s]{6,20}$/.test(tel)) {
      errs.telefono = 'Revisá el formato del teléfono.';
    }
    if (email) {
      const repetido = usuarios.find(
        (u) => u.id !== editingId && (u.email || '').toLowerCase() === email.toLowerCase()
      );
      if (repetido) errs.email = 'Ese email ya lo tiene otro cliente.';
    }
    if (dni) {
      const repetido = usuarios.find((u) => u.id !== editingId && (u.dni || '') === dni);
      if (repetido) errs.dni = 'Ese DNI ya está cargado en otro cliente.';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (guardando) return;

    const errs = validar();
    setErroresCampo(errs);
    if (Object.keys(errs).length > 0) {
      setFormError('Revisá los campos marcados.');
      const primeroConError = Object.keys(errs)[0];
      const campo = modalRef.current && modalRef.current.querySelector(`[name="${primeroConError}"]`);
      if (campo) campo.focus();
      return;
    }

    setGuardando(true);
    setFormError('');
    try {
      // La base no acepta strings vacíos en columnas nullable: van como null
      // para no dejar "" donde debería decir "sin cargar".
      const clienteData = { rol: 'usuario' };
      CAMPOS.forEach((campo) => {
        clienteData[campo] = (formData[campo] || '').trim() || null;
      });

      if (editingId) {
        const { error: err } = await supabase
          .from('usuarios')
          .update(clienteData)
          .eq('id', editingId);
        if (err) throw err;
        setUsuarios((prev) =>
          prev.map((u) => (u.id === editingId ? { ...u, ...clienteData } : u)).sort(porNombre)
        );
        setAviso('Cliente actualizado.');
      } else {
        const { data, error: err } = await supabase
          .from('usuarios')
          .insert([clienteData])
          .select();
        if (err) throw err;
        const nuevo = data && data[0];
        if (nuevo) setUsuarios((prev) => [...prev, nuevo].sort(porNombre));
        setAviso(`Cliente "${clienteData.nombre}" creado.`);
      }

      setModalAbierto(false);
      setFormData({ ...FORMULARIO_VACIO });
      setEditingId(null);
    } catch (err) {
      setFormError(
        `No se pudo ${editingId ? 'actualizar' : 'crear'} el cliente: ` +
          (err.message || JSON.stringify(err))
      );
    } finally {
      setGuardando(false);
    }
  };

  const handleDelete = async (cliente) => {
    if (!window.confirm(`¿Eliminar a ${cliente.nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      const { error: err } = await supabase.from('usuarios').delete().eq('id', cliente.id);
      if (err) throw err;
      setUsuarios((prev) => prev.filter((u) => u.id !== cliente.id));
      if (selectedCliente && selectedCliente.id === cliente.id) setSelectedCliente(null);
      setAviso('Cliente eliminado.');
    } catch (err) {
      const code = (err?.code || '').toLowerCase();
      const msg = (err?.message || '').toLowerCase();
      if (code === '23503' || msg.includes('violates foreign key')) {
        setError('No se puede eliminar: el cliente tiene registros relacionados (reservas, órdenes o sesiones).');
      } else {
        setError('No se pudo eliminar el cliente: ' + (err.message || JSON.stringify(err)));
      }
    }
  };

  const abrirFicha = (cliente) => setSelectedCliente(cliente);

  const resumen = busqueda.trim()
    ? `${usuariosFiltrados.length} de ${usuarios.length} ${usuarios.length === 1 ? 'cliente' : 'clientes'}`
    : `${usuarios.length} ${usuarios.length === 1 ? 'cliente' : 'clientes'}`;

  return (
    <div className={styles.root}>
      {/* Ficha del cliente */}
      {selectedCliente && (
        <FichaCliente
          cliente={selectedCliente}
          onClienteActualizado={actualizarClienteEnLista}
          onClose={() => setSelectedCliente(null)}
        />
      )}

      {/* ── Encabezado + acción de alta ─────────────────────────────────── */}
      <div className={styles.toolbar}>
        <div className={styles.titulos}>
          <h2 className={styles.title}>
            Gestión de Clientes
            {usuarios.length > 0 && <span className={styles.countPill}>{usuarios.length}</span>}
          </h2>
          <p className={styles.subtitle}>
            Abrí la ficha de un cliente para ver su servicio, reservas, sesiones e inspiraciones, y
            generar el contrato.
          </p>
        </div>

        <div className={styles.toolbarAcciones}>
          <button type="button" className={styles.btnNuevo} onClick={abrirNuevo}>
            <Plus size={18} strokeWidth={2.5} aria-hidden="true" />
            Nuevo cliente
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          <AlertTriangle size={17} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {aviso && (
        <div className={styles.aviso} role="status">
          <CheckCircle2 size={17} aria-hidden="true" />
          <span>{aviso}</span>
        </div>
      )}

      {/* ── Listado ─────────────────────────────────────────────────────── */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Listado</h3>

          {usuarios.length > 0 && (
            <div className={styles.searchBar}>
              <Search size={17} className={styles.searchIcon} aria-hidden="true" />
              <input
                type="search"
                className={styles.searchInput}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, DNI, teléfono, email o dirección…"
                aria-label="Buscar clientes"
              />
              {busqueda && (
                <button
                  type="button"
                  className={styles.btnClear}
                  onClick={() => setBusqueda('')}
                  aria-label="Limpiar búsqueda"
                >
                  <X size={15} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <span className={styles.spinner} aria-hidden="true" />
            Cargando clientes…
          </div>
        )}

        {!isLoading && usuarios.length === 0 && (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>
              <UserPlus size={26} aria-hidden="true" />
            </span>
            <p className={styles.emptyTitle}>Todavía no hay clientes</p>
            <p className={styles.emptyText}>
              Cargá el primero con el botón «Nuevo cliente». Después podés abrir su ficha para
              elegirle el servicio y armar el contrato.
            </p>
            <div className={styles.emptyCta}>
              <button type="button" className={styles.btnNuevo} onClick={abrirNuevo}>
                <Plus size={18} strokeWidth={2.5} aria-hidden="true" />
                Nuevo cliente
              </button>
            </div>
          </div>
        )}

        {!isLoading && usuarios.length > 0 && usuariosFiltrados.length === 0 && (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>
              <Search size={24} aria-hidden="true" />
            </span>
            <p className={styles.emptyTitle}>Sin resultados</p>
            <p className={styles.emptyText}>Ningún cliente coincide con «{busqueda}».</p>
          </div>
        )}

        {usuariosFiltrados.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.countPlain}>{resumen}</caption>
              <thead>
                <tr>
                  <th scope="col">Cliente</th>
                  <th scope="col">DNI</th>
                  <th scope="col">Teléfono</th>
                  <th scope="col" className={styles.colOculta}>
                    Dirección
                  </th>
                  <th scope="col" className={styles.thAcciones}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((cliente) => (
                  <tr
                    key={cliente.id}
                    tabIndex={0}
                    onClick={() => abrirFicha(cliente)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        abrirFicha(cliente);
                      }
                    }}
                  >
                    <td>
                      <div className={styles.cellNombre}>
                        <span className={styles.avatar} aria-hidden="true">
                          {iniciales(cliente.nombre)}
                        </span>
                        <span>
                          <span className={styles.nombreText}>{cliente.nombre || 'Sin nombre'}</span>
                          {cliente.email && <span className={styles.nombreMail}>{cliente.email}</span>}
                        </span>
                      </div>
                    </td>
                    <td>
                      {cliente.dni ? (
                        <span className={`${styles.badge} ${styles.badgeOk}`}>{cliente.dni}</span>
                      ) : (
                        <span className={`${styles.badge} ${styles.badgeWarn}`}>Falta</span>
                      )}
                    </td>
                    <td>
                      {cliente.telefono ? (
                        cliente.telefono
                      ) : (
                        <span className={`${styles.badge} ${styles.badgeWarn}`}>Falta</span>
                      )}
                    </td>
                    <td className={styles.colOculta}>
                      {cliente.direccion || (
                        <span className={`${styles.badge} ${styles.badgeWarn}`}>Falta</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.acciones} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={styles.btnFicha}
                          onClick={() => abrirFicha(cliente)}
                          title="Ver la ficha completa del cliente"
                        >
                          <FileText size={14} aria-hidden="true" />
                          Ficha
                        </button>
                        <button
                          type="button"
                          className={`${styles.iconBtn} ${styles.iconBtnWarn}`}
                          onClick={() => abrirEdicion(cliente)}
                          aria-label={`Editar a ${cliente.nombre || 'este cliente'}`}
                          title="Editar datos"
                        >
                          <Pencil size={15} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                          onClick={() => handleDelete(cliente)}
                          aria-label={`Eliminar a ${cliente.nombre || 'este cliente'}`}
                          title="Eliminar cliente"
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className={styles.hint}>
          <AlertTriangle size={15} aria-hidden="true" />
          <span>
            Hacé clic en cualquier fila para abrir la ficha. Ahí están el servicio que eligió, sus
            reservas, sesiones e inspiraciones, y te arma el contrato con lo que falte.
          </span>
        </p>
      </div>

      {/* ── Modal de alta / edición ─────────────────────────────────────── */}
      {modalAbierto && (
        <div
          className={styles.overlay}
          onMouseDown={cerrarModal}
          role="presentation"
        >
          <div
            ref={modalRef}
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-modal-cliente"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHead}>
              <div>
                <h2 id="titulo-modal-cliente" className={styles.modalTitle}>
                  {editingId ? 'Editar cliente' : 'Nuevo cliente'}
                </h2>
                <p className={styles.modalSub}>
                  {editingId
                    ? 'Modificá los datos y guardá los cambios.'
                    : 'Cargá lo que tengas. Lo que falte lo completás después desde la ficha.'}
                </p>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={cerrarModal}
                aria-label="Cerrar"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.modalBody}>
              {formError && (
                <div className={styles.error} role="alert">
                  <AlertTriangle size={17} aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              )}

              <form id="form-alta-cliente" className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="alta-nombre">
                    Nombre<span className={styles.required}>*</span>
                  </label>
                  <input
                    ref={primerCampoRef}
                    id="alta-nombre"
                    name="nombre"
                    type="text"
                    className={`${styles.input} ${erroresCampo.nombre ? styles.inputInvalid : ''}`}
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Nombre y apellido"
                    aria-invalid={erroresCampo.nombre ? 'true' : 'false'}
                    aria-describedby={erroresCampo.nombre ? 'err-nombre' : undefined}
                  />
                  {erroresCampo.nombre && (
                    <span id="err-nombre" className={styles.fieldError}>
                      {erroresCampo.nombre}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="alta-dni">
                    DNI
                  </label>
                  <input
                    id="alta-dni"
                    name="dni"
                    type="text"
                    className={`${styles.input} ${erroresCampo.dni ? styles.inputInvalid : ''}`}
                    value={formData.dni}
                    onChange={handleChange}
                    placeholder="33.966.161"
                    aria-invalid={erroresCampo.dni ? 'true' : 'false'}
                    aria-describedby={erroresCampo.dni ? 'err-dni' : undefined}
                  />
                  {erroresCampo.dni ? (
                    <span id="err-dni" className={styles.fieldError}>
                      {erroresCampo.dni}
                    </span>
                  ) : (
                    <span className={styles.fieldHint}>Se usa en el contrato.</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="alta-email">
                    Email
                  </label>
                  <input
                    id="alta-email"
                    name="email"
                    type="email"
                    className={`${styles.input} ${erroresCampo.email ? styles.inputInvalid : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="cliente@email.com"
                    aria-invalid={erroresCampo.email ? 'true' : 'false'}
                    aria-describedby={erroresCampo.email ? 'err-email' : undefined}
                  />
                  {erroresCampo.email && (
                    <span id="err-email" className={styles.fieldError}>
                      {erroresCampo.email}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="alta-telefono">
                    Teléfono
                  </label>
                  <input
                    id="alta-telefono"
                    name="telefono"
                    type="tel"
                    className={`${styles.input} ${erroresCampo.telefono ? styles.inputInvalid : ''}`}
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="11 5555 5555"
                    aria-invalid={erroresCampo.telefono ? 'true' : 'false'}
                    aria-describedby={erroresCampo.telefono ? 'err-telefono' : undefined}
                  />
                  {erroresCampo.telefono && (
                    <span id="err-telefono" className={styles.fieldError}>
                      {erroresCampo.telefono}
                    </span>
                  )}
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.label} htmlFor="alta-direccion">
                    Dirección
                  </label>
                  <input
                    id="alta-direccion"
                    name="direccion"
                    type="text"
                    className={styles.input}
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Calle, número, piso, ciudad"
                  />
                  <span className={styles.fieldHint}>Va como domicilio en el contrato.</span>
                </div>
              </form>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={cerrarModal}
                disabled={guardando}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="form-alta-cliente"
                className={styles.btnPrimary}
                disabled={guardando}
              >
                {guardando ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clientes;